import { useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { clubInfo } from "@/lib/mbti";
import { Upload, Trash2, Music, FileText, Link2, Save, X } from "lucide-react";

const CLUBS = ["Diplomatas", "Analistas", "Sentinelas", "Exploradores"];

const ICON = { audio: Music, link: Link2 };

/* Editar um tema da biblioteca: título, nomes dos ficheiros, apagar e adicionar ficheiros. */
export default function LibraryEditor({ item, onClose, onDone }) {
  const [title, setTitle] = useState(item?.title || "");
  const [files, setFiles] = useState((item?.files || []).map(({ token, filename, kind }) => ({ token, filename, kind })));
  const [busy, setBusy] = useState(null);
  const [clubs, setClubs] = useState(item?.clubs || CLUBS);
  const flip = (c) => setClubs((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));

  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy("upload");
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post("/uploads/material", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((cur) => [...cur, { token: r.data.token, filename: r.data.filename, kind: r.data.kind }]);
      } catch (err) { toast.error(`${file.name}: ${formatApiError(err)}`); }
    }
    setBusy(null);
  };

  const save = async () => {
    setBusy("save");
    try {
      if (item) await api.put(`/library/${item.source}/${item.id}`, { title, files, clubs });
      else await api.post("/library/topics", { title, files, clubs });
      toast.success(item ? "Biblioteca atualizada" : "Tema criado na biblioteca");
      onDone();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(null);
  };

  const rename = (token, filename) => setFiles((cur) => cur.map((f) => (f.token === token ? { ...f, filename } : f)));

  return (
    <div className="space-y-4" data-testid={`library-editor-${item?.id || "new"}`}>
      <div>
        <label className="kw-overline text-[10px] block mb-1">Título</label>
        <input className="kw-input" value={title} maxLength={150} onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex.: Guiões de angariação" data-testid="library-edit-title" />
      </div>
      <div>
        <label className="kw-overline text-[10px] block mb-2">Pasta dos clubes</label>
        <div className="flex flex-wrap gap-2">
          {CLUBS.map((c) => {
            const on = clubs.includes(c);
            const color = clubInfo(c)?.color || "#E50000";
            return (
              <button key={c} onClick={() => flip(c)} data-testid={`library-edit-club-${c}`}
                className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border transition-colors"
                style={on ? { borderColor: color, color, background: `${color}14` } : { borderColor: "#333", color: "#555" }}>
                {c}
              </button>
            );
          })}
        </div>
      </div>
      <div className="space-y-1.5">
        {files.map((f) => {
          const Icon = ICON[f.kind] || FileText;
          return (
            <div key={f.token} className="flex items-center gap-2 bg-[#0F0F0F] border border-[#1F1F1F] px-3 py-1.5">
              <Icon className="w-4 h-4 text-[#E50000] shrink-0" />
              <input className="bg-transparent flex-1 text-sm outline-none py-1 border-b border-transparent focus:border-[#333]"
                value={f.filename} maxLength={200} onChange={(e) => rename(f.token, e.target.value)}
                data-testid={`library-edit-name-${f.token}`} />
              <button onClick={() => setFiles(files.filter((x) => x.token !== f.token))} title="Apagar da biblioteca"
                className="text-[#555] hover:text-[#E50000]" data-testid={`library-edit-remove-${f.token}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
        {files.length === 0 && <p className="text-xs text-[#EAB308]">Sem ficheiros — o tema deixa de aparecer na biblioteca.</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        <label className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5 cursor-pointer">
          <Upload className="w-3 h-3" /> {busy === "upload" ? "A carregar…" : "Adicionar ficheiros"}
          <input type="file" multiple accept=".pdf,.pptx,.docx,.png,.jpg,.jpeg,.mp3,.m4a,.wav,.ogg,.aac" className="hidden"
            onChange={upload} disabled={!!busy} data-testid="library-edit-upload" />
        </label>
        <button onClick={save} disabled={!!busy || title.trim().length < 2 || clubs.length === 0 || (!item && files.length === 0)}
          className="kw-btn-primary !py-2 text-[10px] inline-flex items-center gap-1.5" data-testid="library-edit-save">
          <Save className="w-3 h-3" /> Guardar
        </button>
        <button onClick={onClose} className="px-4 py-2 border border-[#333] text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white inline-flex items-center gap-1.5"
          data-testid="library-edit-cancel">
          <X className="w-3 h-3" /> Cancelar
        </button>
      </div>
      {item && item.source !== "topic" && (
        <p className="text-[10px] text-[#737373]">As alterações só mudam a biblioteca — os emails já enviados não mudam.</p>
      )}
    </div>
  );
}
